// ════════════════════════════════════════════════════════════════════════════
//  ManDana — resolving a ref
//  ---------------------------------------------------------------------------
//  One question, asked once: given an MdRecordRef, what is this record called,
//  what does it say, and which part of a life is it in?
//
//  WHY THIS IS ITS OWN MODULE. md-record.ts promises "no DOM, no React, no
//  store", and that promise is what lets it load before everything and be
//  asserted without a browser. Resolving cannot keep it: a day record is found
//  in a day map the app already holds, a project in its own document, a domain
//  in its own. So the ref (pure, testable, zero dependencies) stays there, and
//  the part that knows the three homes lives here — after md-domain.ts and
//  md-project.ts, which are the homes.
//
//  THE POINT OF IT. Every surface above the day — a board holding a record, a
//  table, a dashboard — otherwise has to learn three kinds of thing and stay in
//  step with all three forever. This is the one function they ask instead, and
//  adding a fourth kind of record is a case here rather than an edit everywhere.
//
//  A REF TO SOMETHING GONE IS NOT AN ERROR. Deleting a project leaves boards
//  pointing at it, and a day record can be deleted on another device while a
//  board here still names it. Null is the answer, every time — never a throw,
//  because the caller is a render and the alternative is a blank screen where a
//  board used to be. Callers are expected to draw an empty slot and move on.
//
//  ASYMMETRY, STATED. A day record is resolved from `ctx.events` and
//  `ctx.allDay` — the maps of every day md-app already holds in memory — rather
//  than from storage, because
//  the caller is a render and re-reading localStorage per element per frame is
//  the one thing that would make this expensive. A project and a domain are read
//  from their own documents, which are single small reads. That difference is
//  the two homes showing through, not an inconsistency.
// ════════════════════════════════════════════════════════════════════════════

/** What any record looks like once it has been found, whatever its home. */
interface MdResolved {
  ref: MdRecordRef;
  type: string;
  title: string;
  text: string;
  domainId: string | null;
  day: string | null;
  /** The file this record names, when it names one. Null for the overwhelming
      majority of records, and null on a device that has never seen the bytes is
      NOT how absence is signalled — the reference is carried by every device;
      only the bytes are local. See md-file.ts. */
  file?: unknown;
  /** The library row when this ref names a Craft. */
  craft?: unknown;
  /** Compatibility field for older board renderers. */
  manifest?: unknown;
  /** Present only on the third answer (0.3): this ref names something that was
      deleted and is still inside its thirty-day buffer. `text` is empty and
      always will be — a trashed record has no contents to hand over — so a
      surface that finds this must draw the placeholder rather than the record.
      `daysLeft` is what the countdown shows; `trashRoot` names the deletion it
      was swept up in, which is what restore takes back. */
  trashed?: boolean;
  deletedAt?: number;
  daysLeft?: number;
  trashRoot?: string;
  /** The project this record joined, when it joined one. Handed over rather
      than re-read: every resolver that answers a domain was already holding
      `pid` to derive it, and a caller that wants the address ABOVE a record —
      the Librarian's Parent column — would otherwise have to reach back into
      the record's home to read the same field a second time. Absent on the
      records whose place is not a project (a pursuit, a domain, a database),
      because for them the question does not arise. */
  projectId?: string | null;
  /** The database currently holding this row, when this ref names one. A
      CONTEXT, never identity — the ref does not carry it, and a record that
      later belongs to several databases will still answer with one home here. */
  databaseId?: string;
  /** When this record was last written, in epoch milliseconds, and 0 when the
      app keeps no clock for it — the ten domains have none, and a built-in
      project's identity has none until somebody writes in it.

      HANDED OVER FOR THE REASON `projectId` IS. Every resolver was already
      holding the row it read the title from, and that row carries the clock; a
      caller that wants to know WHEN — the Librarian's Last written column —
      would otherwise reach back into each of seven homes to read a field the
      resolver had in its hand. Zero rather than null so callers can compare
      without a guard, and blank rather than 1970 is the rendering caller's job.

      A RECORD'S CLOCK IS THE LATEST OF ITS DOCUMENTS. A pursuit's index row and
      its writing are two documents with two `at`s; the record was last written
      at the later of them, so that is what this answers. */
  at: number;
  /** True when this record's STRUCTURE is the app's and not the user's: the
      IQAMA pursuit and the built-in projects. It says nothing about the writing
      inside them, which is the user's like anyone's, and it is deliberately not
      set on the ten domains — a domain is app-defined too, but refusing a user's
      own column about a domain would be a lock reaching past what it is for. */
  locked?: boolean;
}

(function (g: any) {
  'use strict';

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

  /* A clock, or none. Every home writes `at` (or `updated`) as a number, but a
     row that arrived from an older build or a hand-edited document may carry
     anything at all, and NaN propagates through every comparison downstream as
     silently as it arrives. Zero is the one value that means "no clock" and
     still compares. */
  function clock(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
  }

  /* A day record, found in the maps md-app holds. Both are keyed by day and
     each value is the array that day's document stores.

     A DAY HAS TWO LISTS, AND BOTH ARE ORDINARY. `ctx.events` is the timed
     records the grid draws; `ctx.allDay` is the shelf above the first prayer
     (md-today.jsx AllDaySection) — a record with no time of day, which is what
     anything dropped onto a board is. This used to read `events` alone, so every
     record on the shelf resolved to nothing and a pin of one drew as "This
     record is no longer here" while the record sat one panel away.

     The timed list is searched FIRST, and that order is stated rather than
     incidental: nothing the app writes puts one uid in both lists, so the tie
     cannot happen today — but "whichever we looked at first" is how a rule
     quietly becomes dependent on the shape of the caller's state. */
  function findOnDay(ctx: any, day: string, uid: string): any {
    const isIt = (r: any) => r && typeof r === 'object' && r.uid === uid;
    for (const map of [ctx && ctx.events, ctx && ctx.allDay]) {
      const list = map && map[day];
      if (!Array.isArray(list)) continue;
      const hit = list.find(isIt);
      if (hit) return hit;
    }
    return null;
  }

  function findAcrossDays(ctx: any, uid: string): { day: string; rec: any } | null {
    const days = new Set<string>();
    for (const map of [ctx && ctx.events, ctx && ctx.allDay]) {
      if (!map || typeof map !== 'object') continue;
      for (const day of Object.keys(map)) if (DAY_RE.test(day)) days.add(day);
    }
    for (const day of Array.from(days).sort()) {
      const rec = findOnDay(ctx, day, uid);
      if (rec) return { day, rec };
    }
    return null;
  }

  function resolveDay(ref: MdRecordRef, ctx: any): MdResolved | null {
    const home = ref.day && DAY_RE.test(ref.day) ? ref.day : null;
    const onHome = home ? findOnDay(ctx, home, ref.id) : null;
    /* A day anchor names where the record lived when the ref was WRITTEN, and
       records move days — a drag on the timeline, a reschedule, a sync from
       another device. So an anchor that misses is a forwarding address, not a
       verdict: search every day before answering null. Only a record found
       nowhere is gone. */
    const anchored = (home && onHome) ? { day: home, rec: onHome }
      : findAcrossDays(ctx, ref.id);
    if (!anchored || !anchored.rec) return null;
    const day = anchored.day;
    const rec: any = anchored.rec;
    /* A record's domain is written as `domain`, and COPIED to `id` when the
       record is turned into a block for the day to draw (md-app.jsx:547,
       md-compose.jsx:558) — which is why md-today.jsx reads the card's tile from
       mdDomainById(e.id). Both forms reach this function: a stored record from
       the day document, and a live one already shaped for rendering. Read
       `domain` first and fall back to `id`, so neither shape resolves to a
       record with no colour. `uid` is identity; `id` here is a context. */
    const own = (typeof rec.domain === 'string' && rec.domain) ? rec.domain
      : ((typeof rec.id === 'string' && rec.id) ? rec.id : null);
    const domainId = g.mdProjectDomainFor ? g.mdProjectDomainFor(rec.pid, own) : own;
    return {
      ref, type: ref.type, day,
      title: g.mdRecordTitle(rec),
      text: g.mdRecordSeedText(rec),
      domainId: domainId || null,
      at: clock(rec.at),
      projectId: rec.pid || null,
      file: rec.file || null,
    };
  }

  function resolveProjectRecord(ref: MdRecordRef, ctx: any): MdResolved | null {
    const rows = Array.isArray(ctx && ctx.projectRecords) ? ctx.projectRecords : [];
    const rec: any = rows.find((row: any) => row && row.uid === ref.id);
    if (!rec) return null;
    const own = typeof rec.domain === 'string' ? rec.domain : null;
    const domainId = g.mdProjectDomainFor ? g.mdProjectDomainFor(rec.pid, own) : own;
    return { ref, type: ref.type, day: null, title: g.mdRecordTitle(rec),
      text: g.mdRecordSeedText(rec), domainId: domainId || null,
      at: clock(rec.at), projectId: rec.pid || null };
  }

  function resolveProject(ref: MdRecordRef): MdResolved | null {
    const p = g.mdProjectById ? g.mdProjectById(ref.id) : null;
    if (!p) return null;
    return {
      ref, type: ref.type, day: null,
      title: p.name,
      text: g.mdProjectTextRead ? g.mdProjectTextRead(ref.id) : '',
      domainId: p.domainId || null,
      /* Two documents, and the later one is the answer: a built-in project has
         no index row and so no `at` of its own, and a user's project keeps one
         that stops moving the moment the writing is edited instead. */
      at: Math.max(clock(p.at),
        clock(g.mdProjectWriting ? g.mdProjectWriting(ref.id).at : 0)),
      locked: !!p.builtin,
    };
  }

  function resolvePursuit(ref: MdRecordRef): MdResolved | null {
    const pursuit = g.mdPursuitById ? g.mdPursuitById(ref.id) : null;
    if (!pursuit) return null;
    return {
      ref, type: ref.type, day: null,
      title: pursuit.name,
      text: g.mdPursuitTextRead ? g.mdPursuitTextRead(ref.id) : '',
      domainId: pursuit.domainId || null,
      at: Math.max(clock(pursuit.at),
        clock(g.mdPursuitWriting ? g.mdPursuitWriting(ref.id).at : 0)),
      locked: !!pursuit.builtin,
    };
  }

  /* A domain's TITLE is its label, not the first line of its writing. The ten
     are named by the app and not by the user — that is the whole point of them
     being fixed — so a domain whose page opens "I want to pray on time" is
     still called Spirit everywhere it is listed. */
  function resolveDomain(ref: MdRecordRef): MdResolved | null {
    const d = g.mdDomainById ? g.mdDomainById(ref.id) : null;
    if (!d) return null;
    return {
      ref, type: ref.type, day: null,
      title: d.label,
      text: g.mdDomainTextRead ? g.mdDomainTextRead(ref.id) : '',
      domainId: d.id,
      /* The ten are code, so the only clock a domain can have is the one on
         the writing inside it. None until somebody writes. */
      at: clock(g.mdDomainWriting ? g.mdDomainWriting(ref.id).at : 0),
    };
  }

  /* A manifestation — the fourth home, and the one this module's header
     anticipated when it said a new kind of record should be a case here rather
     than an edit everywhere.

     ITS TEXT IS EMPTY, AND THAT IS DELIBERATE. Every other resolved thing
     answers `text` with writing, and callers render it: a card draws it, a
     search reads it. A manifestation's source is CODE, and handing it back
     through the field that means "writing" invites exactly one bug — some
     surface, now or in a year, rendering a program as prose. What it is called
     is public; what it says is nobody's business but the frame's, and the frame
     gets it from the file store rather than from here. */
  function resolveCraft(ref: MdRecordRef): MdResolved | null {
    const m = g.mdCraftById ? g.mdCraftById(ref.id) : (g.mdManifestById ? g.mdManifestById(ref.id) : null);
    if (!m) return null;
    return {
      ref, type: ref.type, day: null,
      title: m.name,
      text: '',
      domainId: null,
      at: clock(m.updated) || clock(m.created),
      craft: m, manifest: m,
    };
  }

  /* ── the two spaces ──────────────────────────────────────────────────────
     A board and a database are records in exactly the sense this module means
     it: something a person made, that has a name, that can be pointed at. They
     were the shelves the resolver stayed silent about, and silence here is not
     neutral — a catalogue that omits two of its kinds is wrong rather than
     incomplete, and the Organizer cannot prune what it cannot see.

     NEITHER HAS WRITING, and both answer `text` with the empty string for the
     reason resolveManifest already argued: a scene is a drawing and a database
     is a table, and handing either back through the field that means "prose"
     invites one surface, some day, to render it as a paragraph.

     A BOARD MAY HAVE A DOMAIN; A DATABASE MAY NOT. A board scoped to a project
     stores that project's id as `pid` — the one upward pointer, the same field
     a day record uses — so its part of life is derivable by the same call. A
     database carries no pointer at all today, so it answers null rather than
     guessing at one from the rows inside it. */
  function resolveBoard(ref: MdRecordRef): MdResolved | null {
    const rows = g.mdBoardIndex ? g.mdBoardIndex() : [];
    const board = Array.isArray(rows) ? rows.find((b: any) => b && b.id === ref.id) : null;
    if (!board) return null;
    const domainId = g.mdProjectDomainFor ? g.mdProjectDomainFor(board.pid, null) : null;
    return { ref, type: ref.type, day: null, title: board.name, text: '',
      domainId: domainId || null, at: clock(board.at),
      projectId: board.pid || null };
  }

  /* Reads the canonical database rather than a name index, because there is no
     name index — the databases ARE the documents. md-database.ts caches the
     canonical read, so a librarian resolving many refs pays for each database
     once, not once per row. */
  function resolveDatabase(ref: MdRecordRef): MdResolved | null {
    const db = g.mdDatabaseRead ? g.mdDatabaseRead(ref.id) : null;
    if (!db) return null;
    return { ref, type: ref.type, day: null, title: db.name, text: '', domainId: null,
      at: clock(db.updated) || clock(db.created) };
  }

  /** Find whatever a ref names. Null when the ref is malformed, names a type
      this build does not know, or points at something that is no longer here. */
  /* A database row — the fifth home. Its title lives in `values.title` and its
     writing in `body`, both owned by md-database.ts; this asks rather than
     reaches in. An untitled row is called what every untitled record is called,
     for the same reason: it is a label in a list, and blank labels are how
     lists become unreadable. */
  function resolveDbRow(ref: MdRecordRef): MdResolved | null {
    const found = g.mdDatabaseRowFind ? g.mdDatabaseRowFind(ref.id) : null;
    if (!found) return null;
    const raw = found.row && found.row.values ? found.row.values.title : '';
    const title = (typeof raw === 'string' && raw.trim()) ? raw.trim() : g.MD_RECORD_PLACEHOLDER;
    return {
      ref, type: ref.type, day: null,
      title,
      text: typeof found.row.body === 'string' ? found.row.body : '',
      domainId: null,
      at: clock(found.row.updated) || clock(found.row.created),
      databaseId: found.databaseId,
    };
  }

  /* The homes, read from storage. The header's asymmetry still holds — a
     caller that already holds the day maps (md-app, a render) passes them and
     nothing here runs. But the database engine and the member picker live
     BELOW md-app and hold no maps, and "resolve returned null because nobody
     handed you state" is not an answer a member row can render. For them the
     homes are read once from the store. Callers resolving MANY refs must build
     this once and pass it along — memberRowsFor does — because mdStoreAll
     walks every storage key. Missing globals (a harness that never loaded
     md-store.js) degrade to empty homes, which resolve to null: the module's
     standing answer for anything it cannot find. */
  function mdRecordHomes(): any {
    return {
      events: g.mdStoreAll ? g.mdStoreAll('events') : null,
      allDay: g.mdStoreAll ? g.mdStoreAll('records') : null,
      projectRecords: g.mdProjectRecordIndex ? g.mdProjectRecordIndex() : null,
    };
  }

  function homesOr(ctx: any): any {
    return (ctx && (ctx.events || ctx.allDay || ctx.projectRecords)) ? ctx : mdRecordHomes();
  }

  function mdRecordResolve(ref: unknown, ctx?: any): MdResolved | null {
    const r = typeof ref === 'string'
      ? g.mdRecordRefParse(ref)
      : (g.mdRecordRefKey(ref) ? g.mdRecordRefParse(g.mdRecordRefKey(ref)) : null);
    if (!r) return null;
    /* Present-first, at every altitude: a live record is never reported as
       trashed, however many old entries still name it. */
    if (r.type === 'project') return resolveProject(r) || resolveTrashed(r);
    if (r.type === 'pursuit') return resolvePursuit(r) || resolveTrashed(r);
    if (r.type === 'domain') return resolveDomain(r) || resolveTrashed(r);
    if (r.type === 'craft' || r.type === 'manifest') {
      return resolveCraft({ ...r, type: 'craft' }) || resolveTrashed(r);
    }
    if (r.type === 'dbrow') return resolveDbRow(r) || resolveTrashed(r);
    if (r.type === 'board') return resolveBoard(r) || resolveTrashed(r);
    if (r.type === 'database') return resolveDatabase(r) || resolveTrashed(r);
    /* Anything day-anchored resolves out of the day map, whatever it calls
       itself — so a day-anchored type a later build introduces already works
       here. A type with no day and no case above is one we cannot find. The
       storage fallback is computed HERE, lazily, so the typed resolves above
       never pay for a key walk they don't need. */
    const homes = homesOr(ctx);
    const placed = resolveDay(r, homes);
    return placed
      || (!r.day ? resolveProjectRecord(r, homes) : null)
      || resolveTrashed(r);
  }

  /* ── THE THIRD ANSWER (0.3) ───────────────────────────────────────────────
     Two answers were enough while a deletion was final: found, or null, and
     every caller drew an empty slot. A deletion is a MOVE now, for thirty days,
     and a card that goes silently blank tells nobody that the thing is one tap
     from coming back.

     So a ref that resolves nowhere is asked of the bin before null is returned.
     What comes back is deliberately thin — the title, the moment of deletion,
     and the days that are left — because a trashed record HAS no contents to
     hand over. `text` is empty for the same reason the resolver has always
     refused to guess: a placeholder that invented a body would be worse than a
     blank one.

     ORDER MATTERS AND IT IS PRESENT-FIRST. Restore writes a newer generation
     and keeps the tombstone (0.4), so a record can be live AND still named by
     the entry it was deleted in. The live copy wins, always. */
  function resolveTrashed(r: any): MdResolved | null {
    if (!g.mdTrashOf) return null;
    const key = `${r.type}:${r.id}`;
    const hit = g.mdTrashOf(key);
    if (!hit) return null;
    return {
      ref: r,
      type: r.type,
      title: (hit.member && typeof hit.member.title === 'string' && hit.member.title)
        || (g.MD_RECORD_PLACEHOLDER || 'New Record'),
      text: '',
      domainId: null,
      day: r.day || null,
      trashed: true,
      deletedAt: hit.deletedAt,
      daysLeft: hit.daysLeft,
      trashRoot: hit.root,
    } as MdResolved;
  }

  /* ── The one candidate source ─────────────────────────────────────────────
     Every door that offers "a record you already wrote" — the database member
     picker, the [[wikilink]] autocomplete — asks THIS function, so no door can
     know a different set of records than another. It walks the homes in the
     order the wiki catalog established: day records, the project backlog,
     pursuits, projects, the parts of life.

     TWO KEYS PER ROW, AND THE DIFFERENCE IS THE POINT. `ref` carries NO day —
     the shape mdWikiTargetKey binds, so every [[wikilink]] already written
     keeps matching. `key` IS day-anchored for a day record — the membership
     form, so a member resolves in one read (and resolveDay's fallback follows
     the record if it later moves days). For everything else the two agree.

     ONE HOME IS STILL OMITTED, AND ONLY ONE. A database row already belongs to
     a database, so offering it here would be cross-database membership arrived
     at by accident — a different door (U-4), not this one.

     Manifestations used to be omitted too, on the grounds that a manifestation
     is code rather than writing. That was an argument about `text`, and it is
     still right about `text` (resolveManifest hands back none); it was never an
     argument about whether somebody can go looking for the thing they made.
     Phase 4 needs every made thing addressable, so manifestations, boards and
     databases are candidates like anything else. */
  function mdRecordCandidates(query?: unknown, ctx?: any): any[] {
    const q = (typeof query === 'string' ? query : '').trim().toLocaleLowerCase();
    const homes = homesOr(ctx);
    const rows: any[] = [];
    const seen = new Set<string>();
    const offer = (row: any) => {
      if (q && !String(row.title).toLocaleLowerCase().includes(q)) return;
      rows.push(row);
    };
    const addDayMap = (map: any) => {
      for (const day of Object.keys(map || {}).sort()) {
        const list = map[day];
        if (!Array.isArray(list)) continue;
        for (const rec of list) {
          if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
          seen.add(rec.uid);
          const type = g.mdRecordType(rec);
          offer({ ref: { type, id: rec.uid }, key: `${type}:${day}:${rec.uid}`,
            title: g.mdRecordTitle(rec), day, subtitle: `${day} · Day record` });
        }
      }
    };
    addDayMap(homes.events);
    addDayMap(homes.allDay);
    for (const rec of (Array.isArray(homes.projectRecords) ? homes.projectRecords : [])) {
      if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
      seen.add(rec.uid);
      const type = g.mdRecordType(rec);
      offer({ ref: { type, id: rec.uid }, key: `${type}:${rec.uid}`,
        title: g.mdRecordTitle(rec),
        domainId: (typeof rec.domain === 'string' && rec.domain) || null,
        subtitle: 'Project backlog' });
    }
    for (const pursuit of (g.mdPursuitIndex ? g.mdPursuitIndex() : [])) {
      offer({ ref: { type: 'pursuit', id: pursuit.id }, key: `pursuit:${pursuit.id}`,
        title: pursuit.name, domainId: pursuit.domainId || null, subtitle: 'Pursuit' });
    }
    for (const project of (g.mdProjectIndex ? g.mdProjectIndex() : [])) {
      offer({ ref: { type: 'project', id: project.id }, key: `project:${project.id}`,
        title: project.name, domainId: project.domainId || null, subtitle: 'Project' });
    }
    for (const domain of (g.MD_DOMAINS || [])) {
      offer({ ref: { type: 'domain', id: domain.id }, key: `domain:${domain.id}`,
        title: domain.label, domainId: domain.id, subtitle: 'Part of life' });
    }
    for (const craft of (g.mdCraftList ? g.mdCraftList() : (g.mdManifestList ? g.mdManifestList() : []))) {
      offer({ ref: { type: 'craft', id: craft.instance },
        key: `craft:${craft.instance}`,
        title: craft.name, domainId: null, subtitle: 'Craft' });
    }
    for (const board of (g.mdBoardIndex ? g.mdBoardIndex() : [])) {
      offer({ ref: { type: 'board', id: board.id }, key: `board:${board.id}`,
        title: board.name,
        domainId: (g.mdProjectDomainFor ? g.mdProjectDomainFor(board.pid, null) : null) || null,
        subtitle: 'Desk' });
    }
    for (const db of (g.mdDatabaseList ? g.mdDatabaseList() : [])) {
      offer({ ref: { type: 'database', id: db.id }, key: `database:${db.id}`,
        title: db.name, domainId: null, subtitle: 'Database' });
    }
    return rows;
  }

  /** Is this record's structure the app's rather than the user's? One question,
      one authority, asked by every surface that could write to a record — the
      database cell path today, the organizer's write contract later. A ref that
      names nothing is not locked: it is simply not there, and every caller
      already refuses that on its own grounds. */
  function mdRecordLocked(refKey: unknown, ctx?: unknown): boolean {
    const found = mdRecordResolve(refKey, ctx);
    return !!(found && found.locked);
  }

  /** What number, if any, this record carries — the same shape of question as
      mdRecordLocked, asked by surfaces that hold a ref and nothing else.

      It is a BRIDGE and not a calculation: the arithmetic lives with the kind
      of record that owns it (md-project.ts today), and this only knows how to
      turn a ref into the thing that can answer. That is what keeps the formula
      engine ignorant of prayer, and what lets a later record kind carry its own
      reading without a second door being cut for it.

      `''` rather than null for the whole reading, because a formula cell shows
      what it is handed and a blank is the honest look for "no number here".
      The parts come back as numbers or null, so arithmetic on them stays
      arithmetic. */
  function mdRecordReading(refKey: unknown, part?: unknown): unknown {
    const parsed = g.mdRecordRefParse ? g.mdRecordRefParse(refKey) : null;
    const reading = parsed && parsed.type === 'project' && g.mdProjectReading
      ? g.mdProjectReading(parsed.id) : null;
    if (part === 'days') return reading ? reading.days : null;
    if (part === 'of') return reading ? reading.of : null;
    if (part === 'measures') return reading ? reading.measures : '';
    return reading ? `${reading.days}/${reading.of}` : '';
  }

  g.mdRecordResolve = mdRecordResolve;
  g.mdRecordLocked = mdRecordLocked;
  g.mdRecordReading = mdRecordReading;
  g.mdRecordHomes = mdRecordHomes;
  g.mdRecordCandidates = mdRecordCandidates;

  // CommonJS escape hatch, following md-record.ts and md-link.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdRecordResolve, mdRecordLocked, mdRecordReading, mdRecordHomes, mdRecordCandidates };
  }
})(typeof window !== 'undefined' ? window : globalThis);
